import type { FillAllocationDto, FillLineDto, OfficerSummaryDto } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHICH SLOT THE SEARCH SHEET IS ON, AND WHERE IT GOES NEXT. [P4]
 *
 *  ⚠️ THIS IS THE NINETY-SECOND TARGET, AS A PURE FUNCTION.
 *
 *  PA_REQUIREMENTS §6.2: "Three letters, Enter, focus jumps to the next `+`. Twelve names
 *  becomes twelve short bursts, not twelve dialogs." The sheet stays open across selections,
 *  so what advances is not the DOM focus — the dialog traps that — but this cursor. Keeping
 *  it here, with no DOM and no React, is what makes the rule testable at all: jsdom performs
 *  no layout and measures no time, but it can be asked whether the twelfth Enter lands on
 *  the twelfth empty slot.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type FillCursor = {
  lineKey: string;
  allocKey: string;
  slotRef: string;
  mode: 'fill' | 'swap';
};

/** A slot is filled if the server says so, or if a staged proposal is sitting in it. */
export type StagedSlots = ReadonlyMap<string, OfficerSummaryDto>;

const isEmpty = (slotRef: string, assignment: unknown, staged: StagedSlots): boolean =>
  assignment === null && !staged.has(slotRef);

/** Every slot on the sheet, flattened in the order the screen renders them. */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2a §5a] A VEHICLE IS NOT A PERSON, SO IT IS NOT IN THE SLOT SEQUENCE.
 *
 *  `fillLineSchema.kind` has carried `'post' | 'vehicle' | 'adhoc'` since [P4] and the fill
 *  screen read it NOWHERE — so a `V:` line rendered exactly like a `P:` one: an officer
 *  avatar, "Add Van, slot 2 of 4", a hover card with somebody's PHONE NUMBER, and "Any rank"
 *  in the search sheet (because a vehicle's `eligibleRanks` is `[]`, which means no
 *  restriction rather than no people).
 *
 *  ⚠️ IT IS FILTERED HERE, IN THE ONE FUNNEL, RATHER THAN AT FOUR CALL SITES. `countNamed`,
 *  `countSlots`, `nextEmptySlot` and `slotPosition` all read this sequence, so a vehicle
 *  disappears from the footer's "n of m named", from the cursor's wrap, and from the position
 *  line in the search sheet in one edit — and a fifth reader added later inherits it.
 *
 *  ⚠️ AND `adhoc` STAYS IN. A one-off column is a column of PEOPLE that names itself
 *  ([D4.3]); only `vehicle` is the thing that cannot hold an officer.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const isStaffable = (line: Pick<FillLineDto, 'kind'>): boolean => line.kind !== 'vehicle';

/**
 * ⚠️ [C3c.2] WHICH ORG UNIT AN ALLOCATION IS ASKED OF — the UNIT when it names one and the DIVISION
 * otherwise, which is `addresseeOf` in `fillRef.util.ts` read off the DTO instead of off the
 * arrangement. It is the grain COMPLETION has (FR-5.3.6b), so it is what a closed-short share is
 * matched against — by `countOutstanding` below and [P7A §4] by the fill row's closed-short line.
 *
 * ⚠️ IT IS DERIVED FROM `unit`/`division` AND NEVER PARSED OUT OF `allocKey`, whose
 * `<divisionId>:<unitId|*>` shape is a MINT FORMAT — §7's identity-from-a-position family,
 * corrected five times in this repository already. [P7A §4] One expression, exported, so the two
 * readers cannot disagree about whose row a declaration belongs to.
 */
export const allocationAddresseeId = (allocation: Pick<FillAllocationDto, 'unit' | 'division'>): string | null =>
  allocation.unit?.id ?? allocation.division?.id ?? null;

export const slotSequence = (
  lines: readonly FillLineDto[],
): { lineKey: string; allocKey: string; slotRef: string; addresseeId: string | null; assignment: unknown }[] =>
  [...lines]
    .filter(isStaffable)
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .flatMap((line) =>
      line.allocations.flatMap((allocation) =>
        allocation.slots.map((slot) => ({
          lineKey: line.lineKey,
          allocKey: allocation.allocKey,
          slotRef: slot.slotRef,
          /* ⚠️ [C3c.2] Which org unit this slot is asked of — see `allocationAddresseeId`. */
          addresseeId: allocationAddresseeId(allocation),
          assignment: slot.assignment?.officer ?? null,
        })),
      ),
    );

export const countNamed = (lines: readonly FillLineDto[], staged: StagedSlots): number =>
  slotSequence(lines).filter((slot) => !isEmpty(slot.slotRef, slot.assignment, staged)).length;

export const countSlots = (lines: readonly FillLineDto[]): number => slotSequence(lines).length;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C3c.2] EMPTY SLOTS THAT STILL HAVE TO BE FILLED — i.e. every unnamed slot whose
 *  addressee has NOT closed its share with a short-fall.
 *
 *  This is the client's half of `notifyNamedOfficers`' `excused` set, and it is here rather
 *  than in the page for the reason the four functions above it are: `slotSequence` is the one
 *  funnel, so vehicles leave the count for free ([C2a §5a]) and a fifth reader inherits that.
 *
 *  ⚠️ IT IS A SECOND EXPRESSION OF A SERVER RULE, AND IT IS THE SAME ONE `countNamed` ALREADY
 *  WAS. `notReady` has mirrored the server's every-position-named refusal since [C3c.1 §6b]
 *  precisely so that the button cannot offer a send the API answers 409 to; this widens the
 *  mirror rather than adding one. `DutyFill.test.tsx` pins the pair.
 *
 *  ⚠️ A SLOT WITH NO ADDRESSEE IS NEVER EXCUSED. `addresseeId` is null only when an allocation
 *  names neither a unit nor a division, which no write path produces — and the safe answer for
 *  a row nobody can be shown to have excused is that it still counts.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const countOutstanding = (
  lines: readonly FillLineDto[],
  staged: StagedSlots,
  excusedAddresseeIds: ReadonlySet<string>,
): number =>
  slotSequence(lines).filter(
    (slot) =>
      isEmpty(slot.slotRef, slot.assignment, staged) &&
      !(slot.addresseeId !== null && excusedAddresseeIds.has(slot.addresseeId)),
  ).length;

/**
 * ⚠️ [PRE-P6 §2b] EVERY OFFICER THIS SHEET ALREADY HOLDS, FOR GREYING THE SEARCH.
 *
 * `candidate.service.ts` answers the same question server-side and stamps
 * `alreadyOnThisDuty` on each candidate — but that answer is as old as the candidate list,
 * which is keyed on `(dutyId, slotRef, search)` and does not refetch when a write lands. The
 * sheet ORs the two, so the server stays the authority and this can only ever grey MORE.
 *
 * `extra` is what the page knows and the cache does not yet: a copy proposal nobody has
 * saved, and this sitting's writes that have not answered.
 *
 * ⚠️ ONE FUNCTION, BECAUSE THERE ARE TWO FILL SURFACES. `pages/DutyFill` and the arrangement
 * detail's `DutyOfficers` both open the same sheet, and [D4.3] moved the mutations into one
 * hook for exactly this reason — a second definition of "who is already on" would diverge,
 * and the copy that mattered would be the one nobody was looking at.
 */
export const namedOfficerIds = (
  lines: readonly FillLineDto[],
  ...extra: readonly Iterable<OfficerSummaryDto>[]
): ReadonlySet<string> =>
  new Set([
    ...lines.flatMap((line) =>
      line.allocations.flatMap((allocation) =>
        allocation.slots.flatMap((slot) => (slot.assignment?.officer ? [slot.assignment.officer.id] : [])),
      ),
    ),
    ...extra.flatMap((group) => [...group].map((officer) => officer.id)),
  ]);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  The next empty slot after `from`, or `null` when that ROW has none left.
 *
 *  ⚠️ [amended PRE-P6] IT NEVER LEAVES THE ROW IT STARTED ON — A DELIBERATE REVERSAL OF
 *  [P4], WHICH WALKED THE WHOLE SHEET.
 *
 *  P4 built one flat sequence over every line and every allocation and wrapped around the
 *  end of it, so filling the last slot of `UB Men` jumped the operator to the first hole in
 *  `SI` with nothing but a live region to say so. That is what makes twelve names one open,
 *  and it is also a surprise: an operator is thinking about ONE POST at a time, and the
 *  sheet moving them to a different one mid-burst is how somebody names a constable into a
 *  driver's slot without noticing.
 *
 *  ⚠️ THE ROW IS THE ALLOCATION, NOT THE LINE. `FillLines` renders one visual row per
 *  (line × allocation) — its own header, its own chips, its own `StatusDot` — and
 *  `slotPosition`'s "slot 2 of 4" already counts within an allocation. Scoping to the line
 *  would still cross a visible boundary, and it would cross an ADDRESSEE, which is worse:
 *  Central's share and North's share of one post are two different divisions' work.
 *
 *  ⚠️ IT STILL WRAPS, INSIDE THE ROW. An operator who starts at the third chip and works
 *  right must be taken back to the hole they left at the first — P4's argument, unchanged
 *  and now bounded.
 *
 *  ⚠️ `null` NO LONGER MEANS "CLOSE THE SHEET". It means "this row is full", and
 *  `pages/DutyFill` holds the cursor where it is and shows a completion panel. The cost of
 *  that is recorded in the CHANGELOG: twelve names across four posts is four opens rather
 *  than one, which is the client's explicit instruction.
 *
 *  ⚠️ A STAGED SLOT COUNTS AS FILLED. Otherwise "copy from last time" followed by a click on
 *  the one remaining `+` would walk the operator back through all twelve chips it just filled.
 *
 *  ⚠️ AND `from` ITSELF IS NEVER RETURNED. The caller has just filled it; handing it back is
 *  an infinite loop wearing the shape of a working feature.
 *
 *  `from === null` is "start me anywhere", and it is the one case that still reads the whole
 *  sheet — nothing in the app calls it that way, because every entry point opens ON a slot.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const nextEmptySlot = (
  lines: readonly FillLineDto[],
  from: FillCursor | null,
  staged: StagedSlots,
): FillCursor | null => {
  const everything = slotSequence(lines);
  const sequence =
    from === null
      ? everything
      : everything.filter((slot) => slot.lineKey === from.lineKey && slot.allocKey === from.allocKey);
  if (sequence.length === 0) return null;

  const start = from === null ? -1 : sequence.findIndex((slot) => slot.slotRef === from.slotRef);

  for (let step = 1; step <= sequence.length; step += 1) {
    const slot = sequence[(start + step + sequence.length) % sequence.length];
    if (slot && isEmpty(slot.slotRef, slot.assignment, staged)) {
      return { lineKey: slot.lineKey, allocKey: slot.allocKey, slotRef: slot.slotRef, mode: 'fill' };
    }
  }

  return null;
};

/** Where a slot sits within its own line, for the sheet's "slot 2 of 4" line. */
export const slotPosition = (
  lines: readonly FillLineDto[],
  cursor: FillCursor,
): { index: number; total: number; line: FillLineDto | null; allocationLabel: string } => {
  const line = lines.find((row) => row.lineKey === cursor.lineKey) ?? null;
  const allocation = line?.allocations.find((row) => row.allocKey === cursor.allocKey) ?? null;
  const index = allocation?.slots.findIndex((slot) => slot.slotRef === cursor.slotRef) ?? -1;

  return {
    index: index < 0 ? 0 : index,
    total: allocation?.slots.length ?? 0,
    line,
    allocationLabel: allocation?.unit?.name ?? allocation?.division?.name ?? '',
  };
};
